/**
 * Shared component for displaying country flag + name + region.
 * Used across tournament cards, lists, and headers for consistent display.
 */

import { MapPin } from 'lucide-react';
import { getCountryByCode, getCountryRegionName } from '@/data/countries';
import type { CountryCode } from '@/data/countries/types';
import { cn } from '@/lib/utils';

type CountryLabelSize = 'sm' | 'md' | 'lg' | 'xl';
type CountryLabelVariant = 'default' | 'overlay' | 'muted';

interface CountryLabelProps {
  code: CountryCode;
  /** Size preset for flag and text */
  size?: CountryLabelSize;
  /** Styling variant */
  variant?: CountryLabelVariant;
  /** Show the region/subregion below the name */
  showRegion?: boolean;
  /** Show map pin icon before region (for headers) */
  showRegionIcon?: boolean;
  /** Additional class names for the container */
  className?: string;
}

const sizeStyles: Record<
  CountryLabelSize,
  { flag: string; name: string; region: string }
> = {
  sm: {
    flag: 'text-xl',
    name: 'text-sm font-semibold',
    region: 'text-[10px]',
  },
  md: {
    flag: 'text-2xl',
    name: 'text-base font-semibold',
    region: 'text-xs',
  },
  lg: {
    flag: 'text-3xl lg:text-4xl',
    name: 'text-lg lg:text-xl font-semibold',
    region: 'text-xs lg:text-sm',
  },
  xl: {
    flag: 'text-5xl',
    name: 'text-2xl font-semibold',
    region: 'text-sm',
  },
};

const variantStyles: Record<
  CountryLabelVariant,
  { container: string; name: string; region: string }
> = {
  default: {
    container: '',
    name: '',
    region: 'text-muted-foreground',
  },
  overlay: {
    container: 'text-white',
    name: 'drop-shadow-md',
    region: 'opacity-80 drop-shadow',
  },
  muted: {
    container: '',
    name: '',
    region: 'text-muted-foreground',
  },
};

/**
 * Gets the display region for a country, prioritizing geographic regions.
 */
export function getCountryDisplayRegion(code: CountryCode): string {
  const country = getCountryByCode(code);
  if (!country) return '';
  return getCountryRegionName(code) || country.subregion || country.continent;
}

export function CountryLabel({
  code,
  size = 'md',
  variant = 'default',
  showRegion = true,
  showRegionIcon = false,
  className,
}: CountryLabelProps) {
  const country = getCountryByCode(code);
  if (!country) return null;

  const sizeStyle = sizeStyles[size];
  const variantStyle = variantStyles[variant];
  const region = getCountryDisplayRegion(code);

  return (
    <div
      className={cn(
        'flex items-center gap-2 lg:gap-3',
        variantStyle.container,
        className,
      )}
    >
      <span className={cn('shrink-0 drop-shadow-lg', sizeStyle.flag)}>
        {country.flag}
      </span>
      <div className="flex-1 min-w-0">
        <h3
          className={cn(
            'font-display leading-tight truncate',
            sizeStyle.name,
            variantStyle.name,
          )}
        >
          {country.name}
        </h3>
        {showRegion && region && (
          <span
            className={cn(
              'truncate block leading-tight',
              sizeStyle.region,
              variantStyle.region,
            )}
          >
            {showRegionIcon ? (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3 h-3 inline" />
                {region}
              </span>
            ) : (
              region
            )}
          </span>
        )}
      </div>
    </div>
  );
}
